/**
 * Copyright (c) 2020
 *
 * Contains the representation of a Subscription
 *
 * @summary subscription class definition
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-06-08 10:21:56
 * Last modified  : 2021-08-17 16:40:37
 */

import Product from '@/models/customersManager/entity/Product.js'
import PricePoint from '@/models/customersManager/entity/PricePoint.js'
import SubscriptionComponent from '@/models/customersManager/entity/SubscriptionComponent'
import CustomerSubscription from '@/models/customersManager/entity/CustomerSubscription'
import SubscriptionStateEnumType from '@/models/customersManager/enum/SubscriptionStateEnumType'

class Subscription {

    /**
     * @type id Integer
     */
    #id

    /**
     * @type chargifyId Integer
     */
    #chargifyId

    /**
     * @type state String
     */
    #state

    /**
     * @type previousState String
     */
    #previousState

    /**
     * @type createdAt Date
     */
    #createdAt

    /**
     * @type updatedAt Date
     */
    #updatedAt

    /**
     * @type trialStartedAt null|Date
     */
    #trialStartedAt

    /**
     * @type trialEndedAt null|Date
     */
    #trialEndedAt

    /**
     * @type activatedAt null|Date
     */
    #activatedAt

    /**
     * @type expiresAt null|Date
     */
    #expiresAt

    /**
     * @type canceledAt null|Date
     */
    #canceledAt

    /**
     * @type currentPeriodStartedAt null|Date
     */
    #currentPeriodStartedAt

    /**
     * @type currentPeriodEndsAt null|Date
     */
    #currentPeriodEndsAt

    /**
     * @type yoodaParameters Object
     */
    #yoodaParameters = []

    /**
     * @type product null|Product
     */
    #product = null

    /**
     * @type pricePoint null|PricePoint
     */
    #pricePoint = null

    /**
     * @type subscriptionComponents SubscriptionComponent[]
     */
    #subscriptionComponents = []

    /**
     * @type customerSubscriptions CustomerSubscription[]
     */
    #customerSubscriptions = []

    /**
     * Not owned by customers-manager data-model but provided by yooda-one-backend
     *
     * @type analyticsRefreshToken null|Object
     */
    #analyticsRefreshToken = null


    /**
     * @param rawSubscription Object
     */
    constructor(rawSubscription = null) {
        if (rawSubscription !== null)  {
            const {
                id,
                chargifyId,
                state,
                previousState,
                createdAt,
                updatedAt,
                trialStartedAt,
                trialEndedAt,
                activatedAt,
                expiresAt,
                canceledAt,
                currentPeriodStartedAt,
                currentPeriodEndsAt,
                yoodaParameters,
                product,
                pricePoint,
                subscriptionComponents,
                customerSubscriptions,
                analyticsRefreshToken,
            } = rawSubscription



            this.id = id
            this.chargifyId = chargifyId
            this.state = state
            this.previousState = previousState
            this.createdAt = createdAt
            this.updatedAt = updatedAt
            this.trialStartedAt = trialStartedAt
            this.trialEndedAt = trialEndedAt
            this.activatedAt = activatedAt
            this.expiresAt = expiresAt
            this.canceledAt = canceledAt
            this.currentPeriodStartedAt = currentPeriodStartedAt
            this.currentPeriodEndsAt = currentPeriodEndsAt
            this.yoodaParameters = yoodaParameters
            this.product = product
            this.pricePoint = pricePoint
            this.subscriptionComponents = subscriptionComponents
            this.customerSubscriptions = customerSubscriptions
            this.analyticsRefreshToken = analyticsRefreshToken
        }
    }
    /**
     * @returns Integer
     */
    get id () {
        return this.#id
    }

    /**
     * @param id String
     * @returns void
     */
    set id (id) {
        this.#id = parseInt(id)
    }

    /**
     * @returns Integer
     */
    get chargifyId () {
        return this.#chargifyId
    }

    /**
     * @param chargifyId String
     * @returns void
     */
    set chargifyId (chargifyId) {
        this.#chargifyId = parseInt(chargifyId)
    }

    /**
     * @returns String
     */
    get state () {
        return this.#state
    }

    /**
     * @param state String
     * @returns void
     */
    set state (state) {
        if (!Object.values(SubscriptionStateEnumType).includes(state)) {
            throw new Error('subscriptionState is not defined')
        }
        this.#state = state
    }

    /**
     * @returns String
     */
    get previousState () {
        return this.#previousState
    }

    /**
     * @param previousState String
     * @returns void
     */
    set previousState (previousState) {
        if (!Object.values(SubscriptionStateEnumType).includes(previousState)) {
            throw new Error('subscriptionPreviousState is not defined')
        }
        this.#previousState = previousState
    }

    /**
     * @returns Date|null
     */
    get createdAt () {
        return this.#createdAt
    }

    /**
     * @param createdAt String
     * @returns void
     */
    set createdAt (createdAt) {
        this.#createdAt = new Date(createdAt)
    }

    /**
     * @returns Date
     */
    get updatedAt () {
        return this.#updatedAt
    }

    /**
     * @param updatedAt String
     * @returns void
     */
    set updatedAt (updatedAt) {
        this.#updatedAt = new Date(updatedAt)
    }

    /**
     * @returns null|Date
     */
    get trialStartedAt () {
        return this.#trialStartedAt
    }

    /**
     * @param trialStartedAt String
     * @returns void
     */
    set trialStartedAt (trialStartedAt) {
        if (trialStartedAt !== null) {
            this.#trialStartedAt = new Date(trialStartedAt)
        } else {
            this.#trialStartedAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get trialEndedAt () {
        return this.#trialEndedAt
    }

    /**
     * @param trialEndedAt String
     * @returns void
     */
    set trialEndedAt (trialEndedAt) {
        if (trialEndedAt !== null) {
            this.#trialEndedAt = new Date(trialEndedAt)
        } else {
            this.#trialEndedAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get activatedAt () {
        return this.#activatedAt
    }

    /**
     * @param activatedAt String
     * @returns void
     */
    set activatedAt (activatedAt) {
        if (activatedAt !== null) {
            this.#activatedAt = new Date(activatedAt)
        } else {
            this.#activatedAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get expiresAt () {
        return this.#expiresAt
    }

    /**
     * @param expiresAt String
     * @returns void
     */
    set expiresAt (expiresAt) {
        if (expiresAt !== null) {
            this.#expiresAt = new Date(expiresAt)
        } else {
            this.#expiresAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get canceledAt () {
        return this.#canceledAt
    }

    /**
     * @param canceledAt String
     * @returns void
     */
    set canceledAt (canceledAt) {
        if (canceledAt !== null) {
            this.#canceledAt = new Date(canceledAt)
        } else {
            this.#canceledAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get currentPeriodStartedAt () {
        return this.#currentPeriodStartedAt
    }

    /**
     * @param currentPeriodStartedAt String
     * @returns void
     */
    set currentPeriodStartedAt (currentPeriodStartedAt) {
        if (currentPeriodStartedAt !== null) {
            this.#currentPeriodStartedAt = new Date(currentPeriodStartedAt)
        } else {
            this.#currentPeriodStartedAt = null
        }
    }

    /**
     * @returns null|Date
     */
    get currentPeriodEndsAt () {
        return this.#currentPeriodEndsAt
    }

    /**
     * @param currentPeriodEndsAt String
     * @returns void
     */
    set currentPeriodEndsAt (currentPeriodEndsAt) {
        if (currentPeriodEndsAt !== null) {
            this.#currentPeriodEndsAt = new Date(currentPeriodEndsAt)
        } else {
            this.#currentPeriodEndsAt = null
        }
    }

    /**
     * @returns object[]
     */
    get yoodaParameters () {
        return this.#yoodaParameters
    }

    /**
     * @param rawYoodaParameters Object[]
     * @returns void
     */
    set yoodaParameters (rawYoodaParameters) {
        this.#yoodaParameters = rawYoodaParameters
    }

    /**
     * @returns Product|null
     */
    get product () {
        return this.#product
    }

    /**
     * @param rawProduct null|Object
     * @returns void
     */
    set product (rawProduct) {
        if (rawProduct !== null && typeof rawProduct !== 'undefined') {
            this.#product = new Product(rawProduct)
        } else {
            this.#product = null
        }
    }

    /**
     * @returns PricePoint|null
     */
    get pricePoint () {
        return this.#pricePoint
    }

    /**
     * @param rawPricePoint null|Object
     * @returns void
     */
    set pricePoint (rawPricePoint) {
        if (rawPricePoint !== null && typeof rawPricePoint !== 'undefined') {
            this.#pricePoint = new PricePoint(rawPricePoint)
        } else {
            this.#pricePoint = null
        }
    }

    /**
     * @returns SubscriptionComponent[]
     */
    get subscriptionComponents () {
        return this.#subscriptionComponents
    }

    /**
     * @param rawSubscriptionComponents Object[]
     * @returns void
     */
    set subscriptionComponents (rawSubscriptionComponents) {
        this.#subscriptionComponents = []
        if (rawSubscriptionComponents !== null && typeof rawSubscriptionComponents !== 'undefined') {
            rawSubscriptionComponents.forEach((rawSubscriptionComponent) => {
                const subscriptionComponent = new SubscriptionComponent(rawSubscriptionComponent)
                this.#subscriptionComponents = [...this.#subscriptionComponents, subscriptionComponent]
            });
        }
    }

    /**
     * @returns SubscriptionComponent[]
     */
    get customerSubscriptions () {
        return this.#customerSubscriptions
    }

    /**
     * @param rawCustomerSubscriptions Object[]
     * @returns void
     */
    set customerSubscriptions (rawCustomerSubscriptions) {
        this.#customerSubscriptions = []
        if (rawCustomerSubscriptions !== null && typeof rawCustomerSubscriptions !== 'undefined') {
            rawCustomerSubscriptions.forEach((rawCustomerSubscription) => {
                const customerSubscription = new CustomerSubscription(rawCustomerSubscription)
                this.#customerSubscriptions = [...this.#customerSubscriptions, customerSubscription]
            });
        }
    }

    /**
     * @returns analyticsRefreshToken|null
     */
    get analyticsRefreshToken () {
        return this.#analyticsRefreshToken
    }

    /**
     * @param analyticsRefreshToken null|Object
     * @returns void
     */
    set analyticsRefreshToken (analyticsRefreshToken) {
        this.#analyticsRefreshToken = analyticsRefreshToken
    }

    /**
     *
     * @returns {boolean}
     */
    get gracePeriodEnded() {
        let dateReference = null
        switch(this.state){
            case SubscriptionStateEnumType.EXPIRED:
                dateReference = this.expiresAt
                break;
            case SubscriptionStateEnumType.CANCELED:
                dateReference = this.canceledAt
                break;
            case SubscriptionStateEnumType.TRIAL_ENDED:
                dateReference = this.trialEndedAt
                break;
        }

        if(dateReference){
            return (Math.floor( (new Date().getTime() - new Date(dateReference).getTime()) / (24 * 60 * 60 * 1000)) > this.yoodaParameters.stackRunningGracePeriod)
        }

        return false
    }


    /**
     * Returns the reference date of end of subscription
     *
     * @returns Date
     */
    get referenceDate () {
        if (this.canceledAt !== null) {
            return this.canceledAt
        } else if (this.expiresAt !== null) {
            return this.expiresAt
        } else if (this.currentPeriodEndsAt !== null) {
            return this.currentPeriodEndsAt
        } else {
            return this.trialEndedAt
        }
    }


}

export default Subscription
